/*
 * Copyright (c) 2024 Pegasystems Inc.
 * All rights reserved.
 */
import { ofType } from 'redux-observable';
import { map, mergeMap } from 'rxjs/operators';
import { EMPTY, Observable } from 'rxjs';

import * as StateManager from '../../store/state/state-manager';
import { processDataToDispatchErrorAction } from '../../store/state/epics/util';
import { postProcess, resetLoading } from '../../store/state/actions/action-creators';
import { isErrorActionType, promiseHandler } from '../common-helper';
import Utils from '../../utils/utils';
import { postProcessOpenActionRequest } from '../open-action-helper';
import constants from '../../constants';
import type { Action } from '../../store/state/state-manager';

import { processCallBacks } from './helper';
import { OPEN_LOCAL_ACTION } from './action-creator';
import { processOpenLocalActionRequest } from './async-request';

const openLocalAction = (action$: Observable<Action>) => {
  let actionMgrID = 0;
  let actionsOut: any[] = [];

  return action$.pipe(
    ofType(OPEN_LOCAL_ACTION),
    map((action: Action) => {
      ({ actionMgrID } = action.payload);
      return action.payload;
    }),
    mergeMap((actionInPayload) => {
      return processOpenLocalActionRequest(actionInPayload, { actionMgrID });
    }),
    mergeMap(async (chainedData) => {
      if (!chainedData) {
        return EMPTY;
      }
      if (isErrorActionType(chainedData.type)) {
        const errorAction = processDataToDispatchErrorAction(chainedData);
        actionsOut.push(errorAction);
        const { context_data: contextData } = StateManager.getDataObject(chainedData.payload.context) || {};
        if (contextData?.isLoading) {
          actionsOut.push(postProcess(resetLoading, chainedData.payload));
        }
        return actionsOut;
      }

      const { actionInPayload, headers, response } = chainedData;
      const { actionID } = actionInPayload;
      /* updating activeActionID, will usefull during finishAssignment */
      Utils.setIn(response, 'data.caseInfo.activeActionID', actionID);

      /* Setting isLocalAction Flag to true after launching local action due to non availability of availableActions */
      Utils.setIn(response.data, constants.CASE_INFO_IS_LOCAL_ACTION, true);
      if (actionInPayload.refreshConditions) {
        Utils.setIn(response.data, constants.CASE_INFO_REFRESH_CONDITIONS, actionInPayload.refreshConditions);
      }

      await postProcessOpenActionRequest(response, actionsOut, { headers, actionInPayload });
      processCallBacks(actionInPayload);

      return actionsOut;
    }),
    map(() => {
      StateManager.dispatch(actionsOut);
      promiseHandler(actionsOut, actionMgrID);
      actionsOut = [];
      return actionsOut;
    })
  );
};

export default openLocalAction;
